import {DESKTOP_BREAKPOINT} from '@/common/scripts/constants';
import {Lethargy} from 'lethargy';
import {throttle} from 'throttle-debounce';
import {Utils} from '@/common/scripts/utils';

const setIsMobile = () => {
    return Utils.getWindowWidth() >= DESKTOP_BREAKPOINT;
};
const isDesktop = setIsMobile();
const MOUSE_SCROLL_DELTA = 100;

class DesignSlider {
    // eslint-disable-next-line max-lines-per-function,max-statements
    constructor(wrapper, parentSliderInstance = null) {
        this._parentSlider = parentSliderInstance;
        this.wrapper = wrapper;
        this.slidesData = JSON.parse(atob(this.wrapper.dataset.json))?.slides || [];
        this.videoElement = this.wrapper.querySelector('.j-design-slider__video');
        this.buttonsWrapper = this.wrapper.querySelector('.j-design-slider__buttons');
        this.buttons = {
            prev: this.wrapper.querySelector('.j-design-slider__prev'),
            next: this.wrapper.querySelector('.j-design-slider__next')
        };
        this.title = {
            wrapper: this.wrapper.querySelector('.j-design-slider__title'),
            prev   : this.wrapper.querySelector('.j-design-slider__title-prev'),
            current: this.wrapper.querySelector('.j-design-slider__title-current'),
            next   : this.wrapper.querySelector('.j-design-slider__title-next')
        };
        this.text = {
            wrapper: this.wrapper.querySelector('.j-design-slider__text'),
            current: this.wrapper.querySelector('.j-design-slider__text-current'),
            next   : this.wrapper.querySelector('.j-design-slider__text-next')
        };
        this.currentSlideIndex = 1;
        this.nextSlideIndex = null;
        this.direction = '';
        this.activeClass = 'is-active';
        this.disabledClass = 'is-disabled';
        this.nextSlideData = {
            video : '',
            poster: '',
            title : '',
            text  : ''
        };
        this._isAutoMode = false;
        this._lethargy = new Lethargy(8, 50);
        this._throttleListener = throttle(300, ({deltaY}) => {
            if (!isDesktop || !this._isAutoMode || !this.wrapper) {
                return;
            }

            // листаем вниз
            if (deltaY > 0) {
                if (this._isAbleScrollParentNext) {
                    this._unlockParentSliderNavigation();
                } else {
                    this._changeSlide('next');
                }
            }
            // листаем вверх
            if (deltaY <= 0) {
                if (this._isAbleScrollParentPrev) {
                    this._unlockParentSliderNavigation();
                } else {
                    this._changeSlide('prev');
                }
            }
        }, {debounceMode: true});
        this._wheelListener = (event) => {
            (event.deltaY === MOUSE_SCROLL_DELTA || this._lethargy.check(event)) && this._throttleListener(event);
        };
        this._keyListener = this._keyListenerEvent.bind(this);
        this._touchStartListener = this._touchStartListenerEvent.bind(this);
        this._touchMoveListener = this._touchMoveListenerEvent.bind(this);
        this._touchEndListener = this._touchEndListenerEvent.bind(this);
        this._touchData = {
            from: {
                x: null,
                y: null
            },
            to: {
                x: null,
                y: null
            }
        };

        this._bindEvents();
    }

    get isAutoModeEnabled() {
        if (this._parentSlider) {
            return this._isAutoMode && this._parentSlider.isAutoModeEnabled;
        }

        return this._isAutoMode;
    }

    get _isAbleScrollParentNext() {
        return !this.isAutoModeEnabled ||
            (this.currentSlideIndex === this.slidesData.length && this._parentSlider?.goNextSlide);
    }

    get _isAbleScrollParentPrev() {
        return !this.isAutoModeEnabled ||
            (this.currentSlideIndex === 1 && this._parentSlider?.goPrevSlide);
    }

    _keyListenerEvent({keyCode}) {
        if (!isDesktop || !this.wrapper) {
            return;
        }

        switch (keyCode) {
            // arrow left
            case 37: {
                this._changeSlide('prev');
                break;
            }
            // arrow up
            case 38: {
                this._unlockParentSliderNavigation();
                this._parentSlider.goPrevSlide();
                break;
            }
            // arrow right
            case 39: {
                this._changeSlide('next');
                break;
            }
            // arrow down
            case 40: {
                this._unlockParentSliderNavigation();
                this._parentSlider.goNextSlide();
                break;
            }
            default: {
                break;
            }
        }
    }

    _touchStartListenerEvent(event) {
        if (!this.wrapper) {
            return;
        }

        this._touchData.from = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY
        };
    }

    _touchMoveListenerEvent(event) {
        if (!this.wrapper) {
            return;
        }

        this._touchData.to = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY
        };
    }

    _touchEndListenerEvent() {
        const dataFrom = {
            x: this._touchData.from.x,
            y: this._touchData.from.y
        };
        const dataTo = {
            x: this._touchData.to.x,
            y: this._touchData.to.y
        };

        if (!this.wrapper || !dataFrom.x || !dataFrom.y || !dataTo.x || !dataTo.y) {
            return;
        }

        const diffX = dataTo.x - dataFrom.x;
        const diffY = dataTo.y - dataFrom.y;

        if (Math.abs(diffX) > Math.abs(diffY)) {
            if (diffX > 0) {
                this._changeSlide('prev');
            } else {
                this._changeSlide('next');
            }
        } else {
            this._unlockParentSliderNavigation();

            if (diffY > 0) {
                this._parentSlider.goPrevSlide();
            } else {
                this._parentSlider.goNextSlide();
            }
        }
    }

    /**
     * Метод для определения видео текущего слайда и его запуск
     * Т.к. у нас нет данных о видео текущего слайда -- тянем их из соседних слайдов
     */
    _setVideoAndPlay() {
        const data = this.currentSlideIndex === this.slidesData.length ?
            // берём предпоследний слайд
            this.slidesData[this.currentSlideIndex - 2] :
            // берём следующий слайд
            this.slidesData[this.currentSlideIndex];

        const direction = this.currentSlideIndex === this.slidesData.length ? 'next' : 'prev';

        if (this.videoElement?.src !== data.video[direction]) {
            this.videoElement.poster = data.poster[direction];
            this.videoElement.src = data.video[direction];
        }

        this.videoElement.play();
    }

    activateAsInnerSlider(activeParentSlide) {
        if (!Utils.isDescendant(this.wrapper, activeParentSlide)) {
            return;
        }

        this._setVideoAndPlay();

        this._parentSlide = activeParentSlide;
        this._isAutoMode = true;

        this._parentSlide?.addEventListener('wheel', this._wheelListener, {passive: true});
        this._parentSlide?.addEventListener('touchstart', this._touchStartListener);
        this._parentSlide?.addEventListener('touchmove', this._touchMoveListener);
        this._parentSlide?.addEventListener('touchend', this._touchEndListener);
        this._parentSlider?.pauseKeyboard();
        document.addEventListener('keydown', this._keyListener, {passive: true});
    }

    removeAutoMode() {
        if (!isDesktop) {
            return;
        }

        this._isAutoMode = false;

        if (this._parentSlider) {
            this._parentSlider.disableAutoMode();
        }

        this._unlockParentSliderNavigation();
        this._parentSlide?.classList.add('is-touched');
    }

    goNextSlide() {
        if (this._isAbleScrollParentNext) {
            this._unlockParentSliderNavigation();
            this._parentSlider.goNextSlide();
        } else {
            this._changeSlide('next');
        }
    }

    goPrevSlide() {
        if (this._isAbleScrollParentPrev) {
            this._unlockParentSliderNavigation();
            this._parentSlider.goPrevSlide();
        } else {
            this._changeSlide('prev');
        }
    }

    _bindEvents() {
        if (!this.videoElement) {
            console.error('Неверная разметка');
        }

        this.videoElement.addEventListener('ended', () => {
            this.buttonsWrapper.classList.remove(this.disabledClass);
        });

        for (const direction in this.buttons) {
            if ({}.hasOwnProperty.call(this.buttons, direction)) {
                if (!this.buttons[direction]) {
                    console.error('Ошибка! Проверьте разметку!');

                    return;
                }

                this.buttons[direction].addEventListener('click', this._changeSlideManual.bind(this, direction));
            }
        }

        if (!isDesktop) {
            this._bindMobileScroll();
        }
    }

    _bindMobileScroll() {
        const mobileListener = () => {
            if (Utils.isInViewport(this.wrapper, 75)) {
                this.videoElement.play();
                document.body.removeEventListener('touchmove', mobileListener);
            }
        };

        document.body.addEventListener('touchmove', mobileListener);
    }

    _changeSlideManual(direction) {
        this.removeAutoMode();
        this._changeSlide(direction);
    }

    _changeSlide(direction) {
        this.direction = direction;

        const multiplier = this.direction === 'next' ? 1 : -1;

        if (this._checkUpdate(multiplier)) {
            this._getNextSlideData();
            this._setNextSlideData();
            this.currentSlideIndex = this.currentSlideIndex + multiplier;
            this._setArrowsStatus();
        }
    }

    _checkUpdate(multiplier) {
        const startIndex = 0;
        const nextSlide = this.currentSlideIndex + multiplier - 1;

        return nextSlide >= startIndex && nextSlide !== this.slidesData.length;
    }

    _getNextSlideData() {
        for (const prop in this.nextSlideData) {
            if ({}.hasOwnProperty.call(this.nextSlideData, prop)) {
                this.nextSlideData[prop] = this.slidesData[this.currentSlideIndex - 1][prop][this.direction];
            }
        }
    }

    _setNextSlideData() {
        // video
        this.videoElement.poster = this.nextSlideData.poster;

        this.buttonsWrapper.classList.add(this.disabledClass);

        // Задержка необходима, чтобы в ноде успел обновиться постер.
        // Если не сделать, то происходит мерцание старым постером
        // Лучшего решения найти и придумать не смог
        setTimeout(() => {
            this.videoElement.src = this.nextSlideData.video;
            this.videoElement.play();
        }, 4);

        // title
        this.title.wrapper.classList.remove('is-next', 'is-prev');
        this.title[this.direction].innerHTML = this.nextSlideData.title;
        this.title.wrapper.classList.add(`is-${this.direction}`);
        setTimeout(() => {
            this.title.current.innerHTML = this.nextSlideData.title;
            this.title.wrapper.classList.remove(`is-${this.direction}`);
        }, 3000);
        // text
        this.text.next.innerHTML = this.nextSlideData.text;
        this.text.wrapper.classList.add(this.activeClass);
        setTimeout(() => {
            this.text.current.innerHTML = this.nextSlideData.text;
            this.text.wrapper.classList.remove(this.activeClass);
        }, 3000);
    }

    _setArrowsStatus() {
        for (const direction in this.buttons) {
            if ({}.hasOwnProperty.call(this.buttons, direction)) {
                const method = this._checkUpdate(direction === 'next' ? 1 : -1) ? 'remove' : 'add';

                this.buttons[direction].classList[method](this.disabledClass);
            }
        }
    }

    _unlockParentSliderNavigation() {
        if (this._parentSlider?.unpauseScroll) {
            this._parentSlider.unpauseScroll();
        }

        if (this._parentSlider?.unpauseKeyboard) {
            this._parentSlider.unpauseKeyboard();
        }

        if (this._parentSlider?.disableInnerSliderFlag) {
            this._parentSlider.disableInnerSliderFlag();
        }

        this._parentSlide?.removeEventListener('wheel', this._wheelListener);
        this._parentSlide?.removeEventListener('touchstart', this._touchStartListener);
        this._parentSlide?.removeEventListener('touchmove', this._touchMoveListener);
        this._parentSlide?.removeEventListener('touchend', this._touchEndListener);
        document.removeEventListener('keydown', this._keyListener);
    }
}

export default DesignSlider;
